import { useCallback, useState, useEffect } from "react";
import { evolveBadges } from "@/lib/badges";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api, { BACKEND_URL } from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import { MilestoneBadgeSmall } from "@/components/MilestoneBadge";
import PresenceDot from "@/components/PresenceDot";
import { getCoverUrl, getProfilePhotoUrl } from "@/lib/mediaUrls";
import { getDisplayPlays, getDisplayPlaysForArtist } from "@/lib/promoPlays";
import CollaboratorChips from "@/components/CollaboratorChips";

// Chart type tabs
const CHART_TABS = [
  { id: "trending", label: "Trending", icon: "🔥", description: "Hot tracks right now" },
  { id: "most-played", label: "Most Played", icon: "▶️", description: "Top by play count" },
  { id: "most-liked", label: "Most Liked", icon: "❤️", description: "Fan favorites" },
  { id: "top-artists", label: "Top Artists", icon: "⭐", description: "Leading creators" }
];

// Badge component
const Badge = ({ type }) => {
  const badges = {
    founder: { label: "Founder", icon: "👑", className: "bg-gradient-to-r from-yellow-400 to-amber-600 text-black" },
    founding_artist: { label: "Founding Artist", icon: "⭐", className: "bg-gradient-to-r from-purple-500 to-pink-600 text-white" },
    spotlight_winner: { label: "Spotlight", icon: "🏆", className: "bg-gradient-to-r from-green-500 to-emerald-600 text-white" },
    admin: { label: "Admin", icon: "⚡", className: "bg-purple-600 text-white" }
  };
  const badge = badges[type];
  if (!badge) return null;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold inline-flex items-center gap-1 ${badge.className}`}>
      <span>{badge.icon}</span>
      {badge.label}
    </span>
  );
};

// Track Chart Item Component
const TrackChartItem = ({ track, rank, onPlay }) => {
  const navigate = useNavigate();
  const fa = Array.isArray(track.featured_artists) ? track.featured_artists : [];
  const isCollab = fa.length > 0;
  const isFeatured = track.status === "featured";
  const featuringNames = fa
    .map((f) => f.artist_name || f.name)
    .filter(Boolean);

  const handlePlay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onPlay) onPlay(track);
  };

  return (
    <Link to={`/track/${track.submission_id}`}>
      <Card
        className="flex items-center gap-4 p-3 bg-gray-900/50 border-purple-900/30 hover:border-purple-500/50 hover:bg-gray-900/70 transition-all group"
        data-testid={`chart-track-${rank}`}
      >
        {/* Rank */}
        <div className={`w-8 h-8 flex items-center justify-center font-bold text-lg ${
          rank <= 3 ? 'text-yellow-400' : 'text-gray-500'
        }`}>
          {rank <= 3 ? ['🥇', '🥈', '🥉'][rank - 1] : rank}
        </div>

        {/* Cover Art */}
        <div className="relative w-14 h-14 rounded overflow-hidden flex-shrink-0">
          <img
            src={getCoverUrl(track)}
            alt={track.track_title}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100&h=100&fit=crop'; }}
          />
          {/* Play overlay */}
          <button
            onClick={handlePlay}
            className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
              <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
              </svg>
            </div>
          </button>
        </div>

        {/* Track Info */}
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-semibold truncate group-hover:text-purple-300 transition-colors">
            {track.track_title || 'Untitled'}
          </h4>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-gray-400 text-sm truncate">{track.artist_display_name}</span>
            {isFeatured && (
              <span
                className="px-1.5 py-0.5 bg-gradient-to-r from-yellow-400 to-amber-500 text-black text-[10px] font-bold rounded-full"
                data-testid={`chart-pill-featured-${track.submission_id}`}
                title="RGM Featured"
              >
                🎖️ Featured
              </span>
            )}
            {isCollab && (
              <span
                className="px-1.5 py-0.5 bg-pink-500/20 text-pink-200 border border-pink-500/30 text-[10px] font-bold rounded-full"
                title={
                  featuringNames.length
                    ? `with ${featuringNames.join(', ')}`
                    : 'Collaboration'
                }
                data-testid={`chart-pill-collab-${track.submission_id}`}
              >
                🤝 Collab
              </span>
            )}
            {isCollab && featuringNames.length > 0 && (
              <span
                className="text-pink-300 text-xs truncate max-w-[160px] hidden sm:inline"
                title={`with ${featuringNames.join(', ')}`}
                data-testid={`chart-pill-feat-${track.submission_id}`}
              >
                feat. {featuringNames.slice(0, 1).join(', ')}
                {featuringNames.length > 1 ? `, +${featuringNames.length - 1}` : ''}
              </span>
            )}
            {track.artist_badges?.slice(0, 1).map(badge => (
              <Badge key={badge} type={badge} />
            ))}
          </div>
          {isCollab && (
            <CollaboratorChips
              featuredArtists={track.featured_artists}
              size="sm"
              maxVisible={3}
              className="mt-1.5"
              testIdSuffix={`chart-${track.submission_id}`}
            />
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 text-sm flex-shrink-0">
          {/* Milestone Badge */}
          <MilestoneBadgeSmall playCount={getDisplayPlays(track)} />
          <div className="flex items-center gap-1 text-gray-400">
            <span>▶️</span>
            <span>{getDisplayPlays(track).toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-1 text-gray-400">
            <span>❤️</span>
            <span>{(track.likes_count || 0).toLocaleString()}</span>
          </div>
          {track.trending_score !== undefined && (
            <div className="flex items-center gap-1 text-orange-400" title="Trending Score">
              <span>🔥</span>
              <span>{track.trending_score}</span>
            </div>
          )}
        </div>
      </Card>
    </Link>
  );
};

// Artist Chart Item Component
const ArtistChartItem = ({ artist, rank, expanded, onToggleExpand }) => {
  const isRgm = Number.isFinite(artist?.rgm_rank_score);
  const score = isRgm ? artist.rgm_rank_score : 0;
  const placements = artist.contest_placements || {};
  const spot = artist.spotlight_placements || {};
  return (
    <div data-testid={`chart-artist-row-${rank}`}>
      <Card
        className="flex items-center gap-4 p-3 bg-gray-900/50 border-purple-900/30 hover:border-purple-500/50 hover:bg-gray-900/70 transition-all group"
        data-testid={`chart-artist-${rank}`}
      >
        {/* Rank */}
        <div className={`w-8 h-8 flex items-center justify-center font-bold text-lg ${
          rank <= 3 ? 'text-yellow-400' : 'text-gray-500'
        }`}>
          {rank <= 3 ? ['🥇', '🥈', '🥉'][rank - 1] : rank}
        </div>

        {/* Avatar */}
        <Link to={`/artist/${artist.user_id}`} className="flex-shrink-0">
          <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-purple-500/30 group-hover:border-purple-500/50 transition-colors">
            {(artist.profile_photo || artist.picture) ? (
              <img
                src={getProfilePhotoUrl(artist)}
                alt={artist.artist_name || artist.name}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold text-xl">
                {(artist.artist_name || artist.name || '?').charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        </Link>

        {/* Artist Info */}
        <Link to={`/artist/${artist.user_id}`} className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-white font-semibold truncate group-hover:text-purple-300 transition-colors">
              {artist.artist_name || artist.name || 'Unknown Artist'}
            </h4>
            {artist.user_id && <PresenceDot userId={artist.user_id} size="sm" />}
            {evolveBadges(artist.badges)?.slice(0, 2).map(badge => (
              <Badge key={badge} type={badge} />
            ))}
          </div>
          {isRgm ? (
            <div className="text-gray-500 text-sm">
              {artist.unique_supporters || 0} supporter{artist.unique_supporters === 1 ? '' : 's'} · {artist.gifts_received || 0} gift{artist.gifts_received === 1 ? '' : 's'}
            </div>
          ) : (
            <div className="text-gray-500 text-sm">
              {artist.track_count} track{artist.track_count !== 1 ? 's' : ''}
            </div>
          )}
        </Link>

        {/* Stats */}
        {isRgm ? (
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="flex flex-col items-center text-gray-400" title="RGM Rank Score">
              <span className="text-white font-black text-lg" data-testid={`chart-artist-score-${rank}`}>
                {score.toLocaleString()}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-purple-300">RGM Score</span>
            </div>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); onToggleExpand(artist.user_id); }}
              className="w-8 h-8 rounded-full bg-gray-800/60 hover:bg-purple-500/20 text-gray-400 hover:text-purple-300 flex items-center justify-center transition-all"
              data-testid={`chart-artist-expand-${rank}`}
              aria-label="Show score breakdown"
              aria-expanded={expanded}
            >
              <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-4 text-sm flex-shrink-0">
            <div className="flex flex-col items-center text-gray-400" title="Plays (real + active feature momentum)">
              <span className="text-white font-semibold">{getDisplayPlaysForArtist(artist).toLocaleString()}</span>
              <span className="text-xs">plays</span>
            </div>
            <div className="flex flex-col items-center text-gray-400" title="Total Likes">
              <span className="text-white font-semibold">{(artist.total_likes || 0).toLocaleString()}</span>
              <span className="text-xs">likes</span>
            </div>
            <div className="flex flex-col items-center text-gray-400" title="Followers">
              <span className="text-white font-semibold">{(artist.follower_count || 0).toLocaleString()}</span>
              <span className="text-xs">followers</span>
            </div>
          </div>
        )}
      </Card>
      {isRgm && expanded && (
        <div
          className="mt-1 mb-2 p-4 rounded-lg bg-gray-900/70 border border-purple-500/20 text-sm"
          data-testid={`chart-artist-breakdown-${rank}`}
        >
          <p className="text-gray-400 text-xs uppercase tracking-widest mb-2">Score Breakdown</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><span className="text-gray-500">Reward Coins:</span> <span className="text-white font-semibold">{(artist.reward_coins_received || 0).toLocaleString()}</span></div>
            <div><span className="text-gray-500">Gifts:</span> <span className="text-white font-semibold">{artist.gifts_received || 0}</span></div>
            <div><span className="text-gray-500">Unique Supporters:</span> <span className="text-white font-semibold">{artist.unique_supporters || 0}</span></div>
            <div><span className="text-gray-500">Paid-Support Gifts:</span> <span className="text-white font-semibold">{artist.paid_support_gifts || 0}</span></div>
            <div><span className="text-gray-500">Contest Entries:</span> <span className="text-white font-semibold">{artist.contest_entries || 0}</span></div>
            <div>
              <span className="text-gray-500">Contest Wins:</span>{' '}
              <span className="text-white font-semibold">
                🥇 {placements.first || 0} · 🥈 {placements.second || 0} · 🥉 {placements.third || 0}
              </span>
            </div>
            <div className="col-span-2 sm:col-span-3">
              <span className="text-gray-500">Spotlight:</span>{' '}
              <span className="text-white font-semibold">
                🥇 {spot.first || 0} · 🥈 {spot.second || 0} · 🥉 {spot.third || 0}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Microphone Icon
const MicIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
  </svg>
);

const ChartsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentUser, setCurrentUser] = useState(null);
  const [activeChart, setActiveChart] = useState(searchParams.get('chart') || 'trending');
  const [tracks, setTracks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  // Visibility-boost: when ON, narrow the track charts to collaboration tracks
  // only. Doesn't apply to the Top Artists tab.
  const [collabsOnly, setCollabsOnly] = useState(searchParams.get('collabs') === '1');
  // RGM Charts R1 — period selector on the Top Artists tab only.
  const [rgmPeriod, setRgmPeriod] = useState(
    (searchParams.get('period') === 'monthly') ? 'monthly' : 'weekly'
  );
  const [rgmMeta, setRgmMeta] = useState({ period_label: null, total: 0, has_more: false, next_cursor: null });
  const [rgmLoadingMore, setRgmLoadingMore] = useState(false);
  const [expandedArtist, setExpandedArtist] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
    } catch (error) {
      setCurrentUser(null);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    fetchChartData(activeChart);
    const next = { chart: activeChart };
    if (collabsOnly) next.collabs = '1';
    if (activeChart === 'top-artists') next.period = rgmPeriod;
    setSearchParams(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChart, collabsOnly, rgmPeriod, setSearchParams]);

  const _unused_checkAuth = async () => {
    // superseded by useCallback version above
    return null;
  };

  const fetchChartData = async (chartType) => {
    setLoading(true);
    setExpandedArtist(null);
    try {
      if (chartType === 'top-artists') {
        // RGM Charts R1 — authoritative rankings endpoint.
        const response = await api.get(
          `/charts/rankings?period=${rgmPeriod}&limit=25`
        );
        const body = response.data || {};
        setArtists(body.artists || []);
        setRgmMeta({
          period_label: body.period_label || null,
          period_start: body.period_start,
          period_end: body.period_end,
          total: body.total || 0,
          has_more: !!body.has_more,
          next_cursor: body.next_cursor || null,
          public_max: body.public_max || 100,
        });
        setTracks([]);
      } else {
        const response = await api.get(`/charts/${chartType}?limit=50`);
        setTracks(response.data.tracks || []);
        setArtists([]);
      }
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
      setTracks([]);
      setArtists([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreRankings = async () => {
    if (!rgmMeta.has_more || !rgmMeta.next_cursor || rgmLoadingMore) return;
    setRgmLoadingMore(true);
    try {
      const response = await api.get(
        `/charts/rankings?period=${rgmPeriod}&limit=25&cursor=${encodeURIComponent(rgmMeta.next_cursor)}`
      );
      const body = response.data || {};
      setArtists(prev => [...prev, ...(body.artists || [])]);
      setRgmMeta({
        period_label: body.period_label || rgmMeta.period_label,
        period_start: body.period_start,
        period_end: body.period_end,
        total: body.total || rgmMeta.total,
        has_more: !!body.has_more,
        next_cursor: body.next_cursor || null,
        public_max: body.public_max || rgmMeta.public_max,
      });
    } catch (error) {
      console.error('Failed to load more rankings:', error);
    } finally {
      setRgmLoadingMore(false);
    }
  };

  const currentTab = CHART_TABS.find(t => t.id === activeChart) || CHART_TABS[0];
  const isArtistsTab = activeChart === 'top-artists';
  const visibleTracks = collabsOnly
    ? tracks.filter(
        (t) =>
          Array.isArray(t.featured_artists) && t.featured_artists.length > 0,
      )
    : tracks;

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="charts-page">
      <SharedNavigation currentUser={currentUser} activePage="charts" />

      {/* Header */}
      <div className="pt-20 px-4 pb-8 bg-gradient-to-b from-purple-900/20 to-transparent">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl font-bold text-white mb-2">Charts</h1>
          <p className="text-gray-400">See what's performing on RGM Studio</p>
        </div>
      </div>

      {/* Chart Tabs */}
      <div className="sticky top-16 z-40 bg-[#08080c]/95 backdrop-blur-md border-b border-purple-900/30">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex gap-1 py-3 overflow-x-auto scrollbar-hide items-center">
            {CHART_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveChart(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                  activeChart === tab.id
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold'
                    : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
                data-testid={`chart-tab-${tab.id}`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
            {/* Visual divider */}
            <span className="hidden sm:inline-block w-px h-6 bg-gray-700/60 mx-2" />
            {/* Collabs Only filter chip — disabled on artist-only charts */}
            <button
              type="button"
              onClick={() => !isArtistsTab && setCollabsOnly((v) => !v)}
              disabled={isArtistsTab}
              aria-pressed={collabsOnly}
              title={
                isArtistsTab
                  ? 'Switch to a track chart to filter by collaborations'
                  : collabsOnly
                  ? 'Showing only tracks with tagged collaborators'
                  : 'Filter to only show collaboration tracks'
              }
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full whitespace-nowrap text-sm font-semibold border transition-all ${
                isArtistsTab
                  ? 'bg-gray-900/40 text-gray-600 border-gray-800 cursor-not-allowed'
                  : collabsOnly
                  ? 'bg-pink-500/20 text-pink-100 border-pink-400/60'
                  : 'bg-gray-800/60 text-pink-200 border-pink-500/30 hover:bg-pink-500/10'
              }`}
              data-testid="chart-collabs-only-chip"
            >
              <span>🤝</span>
              <span>Collabs Only</span>
              {!isArtistsTab && collabsOnly && visibleTracks.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-black/40 text-[10px] text-pink-100">
                  {visibleTracks.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Chart Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Chart Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <span>{currentTab.icon}</span>
              {currentTab.label}
            </h2>
            <p className="text-gray-500 text-sm">{currentTab.description}</p>
          </div>
          <span className="text-gray-500 text-sm">
            {loading ? 'Loading...' : `${activeChart === 'top-artists' ? artists.length : visibleTracks.length} entries`}
          </span>
        </div>

        {/* Chart List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Card key={i} className="p-4 bg-gray-900/50 border-purple-900/30 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-gray-700 rounded" />
                  <div className="w-14 h-14 bg-gray-700 rounded" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-700 rounded w-1/3" />
                    <div className="h-3 bg-gray-700 rounded w-1/4" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : activeChart === 'top-artists' ? (
          artists.length === 0 ? (
            <Card className="p-12 text-center bg-gray-900/50 border-purple-900/30">
              <div className="text-5xl mb-4">🎤</div>
              <h3 className="text-xl font-semibold text-white mb-2">No artists yet</h3>
              <p className="text-gray-400 mb-6">Be the first to submit your music and top the charts!</p>
              <Link to="/login">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  Submit Music
                </Button>
              </Link>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="artist-chart-list">
              {activeChart === 'top-artists' && (
                <div
                  className="flex flex-wrap items-center justify-between gap-3 mb-2"
                  data-testid="rgm-charts-period-controls"
                >
                  <div className="inline-flex bg-gray-900/60 border border-purple-500/20 rounded-full p-1">
                    {[
                      { id: 'weekly', label: 'This Week' },
                      { id: 'monthly', label: 'This Month' },
                    ].map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setRgmPeriod(p.id)}
                        className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${
                          rgmPeriod === p.id
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                            : 'text-gray-400 hover:text-white'
                        }`}
                        data-testid={`rgm-charts-period-${p.id}`}
                        aria-pressed={rgmPeriod === p.id}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  {rgmMeta.period_label && (
                    <span className="text-gray-500 text-xs" data-testid="rgm-charts-period-label">
                      {rgmMeta.period_label}
                    </span>
                  )}
                </div>
              )}
              {artists.map((artist, idx) => (
                <ArtistChartItem
                  key={artist.user_id}
                  artist={artist}
                  rank={artist.rank || idx + 1}
                  expanded={expandedArtist === artist.user_id}
                  onToggleExpand={(uid) => setExpandedArtist(prev => prev === uid ? null : uid)}
                />
              ))}
              {activeChart === 'top-artists' && rgmMeta.has_more && (
                <div className="flex justify-center pt-2">
                  <Button
                    onClick={loadMoreRankings}
                    disabled={rgmLoadingMore}
                    variant="outline"
                    className="border-purple-500/40 text-purple-200 hover:bg-purple-500/10"
                    data-testid="rgm-charts-load-more"
                  >
                    {rgmLoadingMore ? 'Loading…' : 'Load More'}
                  </Button>
                </div>
              )}
            </div>
          )
        ) : (
          visibleTracks.length === 0 ? (
            <Card className="p-12 text-center bg-gray-900/50 border-purple-900/30">
              <div className="text-5xl mb-4">{collabsOnly ? '🤝' : '🎵'}</div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {collabsOnly ? 'No collaboration tracks here yet' : 'No tracks yet'}
              </h3>
              <p className="text-gray-400 mb-6">
                {collabsOnly
                  ? 'Tag a featured artist on your next upload and land in this chart.'
                  : 'Be the first to submit your music and top the charts!'}
              </p>
              {collabsOnly ? (
                <Button
                  onClick={() => setCollabsOnly(false)}
                  variant="outline"
                  className="border-pink-500/40 text-pink-200 hover:bg-pink-500/10"
                  data-testid="chart-clear-collabs-filter"
                >
                  Show all tracks
                </Button>
              ) : (
                <Link to="/login">
                  <Button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                    Submit Music
                  </Button>
                </Link>
              )}
            </Card>
          ) : (
            <div className="space-y-3" data-testid="track-chart-list">
              {visibleTracks.map((track, idx) => (
                <TrackChartItem key={track.submission_id} track={track} rank={idx + 1} />
              ))}
            </div>
          )
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-purple-900/30 bg-black/80 backdrop-blur-xl py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/rgm-logo.png" alt="RGM Studio" className="h-8 w-auto object-contain" />
            </Link>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <Link to="/explore" className="hover:text-purple-400 transition-colors">
                Explore
              </Link>
              <Link to="/magazine" className="hover:text-purple-400 transition-colors">
                Magazine
              </Link>
              <Link to="/community-guidelines" className="hover:text-purple-400 transition-colors">
                Guidelines
              </Link>
            </div>
            <p className="text-gray-500 text-sm">© 2025 RGM Studio™</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ChartsPage;
